import {
    type FieldActions,
    type FieldInput,
    type FieldInputConvertFn,
    type FieldInputParseFn,
    Scope,
    useFieldInput,
    useFieldSelector,
    useForm,
    useMetadataSnapshot,
    useStableCallback,
    type ValidationError,
    ValidationErrorDisplayMode
} from "@ngt-form/core";
import {
    type ComponentProps,
    type ComponentType,
    type Dispatch,
    type FocusEvent,
    type ReactNode,
    type SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    FileState,
    GetTemporaryFileInfoRequest,
    type LookupOption,
    RemoveTemporaryFileRequest,
    type SourceDocument,
    type TemporaryFile,
    type TemporaryFileInformation,
    type UploadTemporaryFileResponse
} from "src/api/Tmd.dtos";
import useServiceStackClient from "src/hooks/api/useServiceStackClient";
import ChunkedUploader from "src/utilities/ChunkedUploader";

type FileUploadInputValue = TemporaryFileInformation | null | undefined;

export type FileUploadInputProps<
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown,
    TComponent extends ComponentType<
        FileUploadInputViewProps<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TFieldValue,
            TLookupItem
        >
    > = ComponentType<
        FileUploadInputViewProps<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TFieldValue,
            TLookupItem
        >
    >
> = {
    onChange?: (
        value: TFieldValue | null | undefined,
        fieldsActions: FieldActions<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TFieldValue,
            TLookupItem
        >
    ) => void;
    onBlur?: (
        e: FocusEvent<unknown>,
        fieldActions: FieldActions<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TFieldValue,
            TLookupItem
        >
    ) => void;
    onFocus?: (
        e: FocusEvent<unknown>,
        fieldActions: FieldActions<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TFieldValue,
            TLookupItem
        >
    ) => void;
    parse?: FieldInputParseFn<TFieldValue, FileUploadInputValue> | null | undefined;
    convert?: FieldInputConvertFn<TFieldValue, FileUploadInputValue> | null | undefined;
    validationErrorDisplayMode?: ValidationErrorDisplayMode | null | undefined;
    label?: ReactNode | null | undefined;
    disabled?: boolean | null | undefined;
    chunk?: boolean | null | undefined;
    chunkSize?: number | null | undefined;
    chunkConcurrencyLimit?: number | null | undefined;
    chunkRetryLimit?: number | null | undefined;
    view?: TComponent | null | undefined;
    viewProps: Omit<
        ComponentProps<TComponent>,
        | "fieldInput"
        | "onChange"
        | "onBlur"
        | "onFocus"
        | "chunk"
        | "chunkSize"
        | "chunkConcurrencyLimit"
        | "chunkRetryLimit"
        | "label"
        | "disabled"
        | "upload"
        | "file"
        | "setFile"
        | "existingFile"
    >;
};

export type FileUploadInputViewProps<
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown
> = {
    fieldInput: FieldInput<
        TFormValue,
        TValidationError,
        TFormMetadata,
        TFieldValue,
        TLookupItem,
        FileUploadInputValue
    >;
    chunk?: boolean | null | undefined;
    chunkSize?: number | null | undefined;
    chunkConcurrencyLimit?: number | null | undefined;
    chunkRetryLimit?: number | null | undefined;
    uploadState?: FileUploadState | null | undefined;
    chunkedUploader: ChunkedUploader;
    existingFile?: FileUploadInputExistingFile | undefined;
    file?: File | undefined;
    disabled?: boolean | null | undefined;
    setFile: Dispatch<SetStateAction<File | undefined>>;
    upload: (file: File | undefined) => Promise<void>;

    onChange?: (
        value: TFieldValue | null | undefined,
        fieldsActions: FieldActions<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TFieldValue,
            TLookupItem
        >
    ) => void;

    onBlur?: (
        e: FocusEvent<unknown>,
        fieldActions: FieldActions<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TFieldValue,
            TLookupItem
        >
    ) => void;

    onFocus?: (
        e: FocusEvent<unknown>,
        fieldActions: FieldActions<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TFieldValue,
            TLookupItem
        >
    ) => void;
};

const ONE_MB = 1024 * 1024;
const FIVE_MB = 5 * ONE_MB;
const TWENTY_MB = 20 * ONE_MB;

/**
 * Creates an empty temporary-file field value with no GUID, progress or metadata.
 */
const createEmptyState = (): TemporaryFileInformation => ({
    state: FileState.Empty,
    guid: undefined,
    progress: undefined,
    temporaryFile: undefined
});

/**
 * Creates a successful temporary-file field value retaining its GUID even without optional metadata.
 */
const createSuccessState = (
    guid: string,
    temporaryFile: TemporaryFile | undefined
): TemporaryFileInformation => ({
    state: FileState.Success,
    guid: guid,
    progress: undefined,
    temporaryFile: temporaryFile
});

export type FileUploadInputExistingFile = {
    id: number;
    name: string;
    contentType: string;
    extension: string;
    fileSize?: number;
    image?: boolean;
    dateUploaded?: string;
};

/**
 * Compares the existing-file projection by identity and all displayed/download identity fields.
 */
const existingFileEquality = (
    left: FileUploadInputExistingFile | undefined,
    right: FileUploadInputExistingFile | undefined
) => {
    if (left === right) {
        return true;
    }

    if (!left) {
        return false;
    }

    if (!right) {
        return false;
    }

    if (
        left.name === right.name &&
        left.contentType === right.contentType &&
        left.extension === right.extension &&
        left.fileSize === right.fileSize &&
        left.image === right.image &&
        left.dateUploaded === right.dateUploaded &&
        left.id === right.id
    ) {
        return true;
    }

    return false;
};

export type FileUploadState = {
    uploadId?: string;
    state: "idle" | "uploading" | "paused" | "cancelling" | "completed" | "failed" | "cancelled";
    progress?: number;
};

/**
 * Coordinates single or chunked temporary uploads with form state and a supplied presentation view.
 * Postconditions: Only the mounted current attempt may publish completion/progress. Existing-file
 * information comes from an initially existing, explicitly downloadable source document. No view
 * renders nothing after hooks initialize. Upload success updates draft temporary-file data, not the
 * persisted source document. Cleanup tracks GUIDs and is deferred to tolerate StrictMode replay.
 */
export const FileUploadInput = <
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TFieldValue = unknown,
    TLookupItem = unknown
>({
    disabled: componentDisabled,
    label: componentLabel,
    parse,
    convert,
    onChange,
    onFocus,
    onBlur,
    validationErrorDisplayMode: componentValidationErrorDisplayMode,
    chunk = true,
    chunkSize = FIVE_MB,
    chunkConcurrencyLimit = 4,
    chunkRetryLimit = 3,
    view,
    viewProps
}: FileUploadInputProps<TFormValue, TValidationError, TFormMetadata, TFieldValue, TLookupItem>) => {
    const fieldInput = useFieldInput<
        TFormValue,
        TValidationError,
        TFormMetadata,
        TFieldValue,
        TLookupItem,
        FileUploadInputValue
    >({
        scope: Scope.Context,
        label: componentLabel,
        validationErrorDisplayMode: componentValidationErrorDisplayMode,
        convert,
        parse,
        disabled: componentDisabled,
        subscription: {
            value: true,
            label: true,
            lookup: false,
            dirty: false,
            focused: false,
            initialValue: false,
            touched: false
        }
    });

    const { actions, snapshot, parseValue, convertValue, disabled } = fieldInput;

    const existingFile = useFieldSelector<
        TFormValue,
        TValidationError,
        TFormMetadata,
        SourceDocument,
        LookupOption,
        FileUploadInputExistingFile | undefined
    >(
        useStableCallback(snapshot => {
            if (
                !snapshot.initialValue ||
                !snapshot.initialValue.exists ||
                snapshot.initialValue.isDownloadable !== true
            ) {
                return null;
            }

            return {
                id: snapshot.initialValue.id,
                contentType: snapshot.initialValue.contentType,
                extension: snapshot.initialValue.extension,
                name: snapshot.initialValue.name,
                dateUploaded: snapshot.initialValue.dateEntered,
                fileSize: snapshot.initialValue.fileSize,
                image: snapshot.initialValue.image
            };
        }
        ),
        { initialValue: true },
        {
            scope: Scope.PropertyPath,
            propertyPath: snapshot.parentPropertyPath,
            prependParentPropertyPath: false,
            equalityFn: existingFileEquality
        }
    );

    const [file, setFile] = useState<File | undefined>(undefined);

    const xhrRef = useRef<XMLHttpRequest>(undefined);

    const client = useServiceStackClient();
    const clientRef = useRef(client);
    clientRef.current = client;

    const mountedRef = useRef(false);
    const attemptRef = useRef(0);
    const activeChunkRef = useRef<{ uploader: ChunkedUploader; id: string } | undefined>(undefined);
    const temporaryFileIdsRef = useRef(new Set<string>());
    const currentTemporaryFileId = convertValue(snapshot.value)?.guid;
    if (currentTemporaryFileId) {
        temporaryFileIdsRef.current.add(currentTemporaryFileId);
    }

    const { value: uploadState } = useMetadataSnapshot<
        TFormValue,
        TValidationError,
        TFormMetadata,
        FileUploadState
    >(
        { value: true },
        {
            scope: Scope.PropertyPath,
            propertyPath: snapshot.propertyPath,
            prependParentPropertyPath: false
        }
    );

    const { setMetadataValue } = useForm();

    /**
     * Publishes upload metadata for the current field path with form notification enabled.
     */
    const setUploadState = useCallback(
        (uploadState: FileUploadState | undefined) => {
            setMetadataValue(snapshot.propertyPath, uploadState, {
                notify: true
            });
        },
        [setMetadataValue, snapshot.propertyPath]
    );

    /**
     * Constructs an uploader for current client/chunk settings; lifecycle cleanup disposes old instances.
     */
    const chunkedUploader = useMemo(
        () => {
            return new ChunkedUploader(client, chunkSize, chunkConcurrencyLimit, chunkRetryLimit);
        },
        [chunkSize, chunkRetryLimit, chunkConcurrencyLimit, client]
    );
    const uploaderRef = useRef(chunkedUploader);
    uploaderRef.current = chunkedUploader;

    /**
     * Checks both mounted lifetime and matching attempt number before accepting asynchronous results.
     */
    const isCurrentAttempt = useCallback(
        (attempt: number) => mountedRef.current && attemptRef.current === attempt,
        []
    );

    useEffect(
        () => {
            return () => {
                // StrictMode replays effects using the same memoized instance.
                void Promise.resolve().then(() => {
                    if (!mountedRef.current || uploaderRef.current !== chunkedUploader) {
                        return chunkedUploader.dispose();
                    }
                }
                );
            };
        },
        [chunkedUploader]
    );

    useEffect(
        () => {
            mountedRef.current = true;

            return () => {
                mountedRef.current = false;
                attemptRef.current++;
                xhrRef.current?.abort();
                xhrRef.current = undefined;
                const temporaryFileIds = [...temporaryFileIdsRef.current];

                // Deferring by one microtask avoids deleting during React StrictMode's
                // development-only effect cleanup/setup cycle.
                void Promise.resolve().then(async () => {
                    if (mountedRef.current) {
                        return;
                    }

                    await Promise.all(
                        temporaryFileIds.map(async temporaryFileId => {
                            try {
                                await clientRef.current.post(
                                    new RemoveTemporaryFileRequest({
                                        guid: temporaryFileId
                                    })
                                );
                                temporaryFileIdsRef.current.delete(temporaryFileId);
                            } catch (error) {
                                console.error(
                                    `Failed to remove temporary file ${temporaryFileId} during unmount:`,
                                    error
                                );
                            }
                        }
                        )
                    );
                }
                );
            };
        },
        []
    );

    /**
     * Updates the parsed field value and dirty/focused/touched flags before invoking onChange.
     * Postconditions: Form notification is requested; callback failures propagate and persistence is external.
     */
    const update = useCallback(
        (newFieldValue: TFieldValue) => {
            actions.setValue(newFieldValue, {
                notify: true,
                updateDirty: true,
                updateFocused: true,
                updateTouched: true
            });

            onChange?.(newFieldValue, actions);
        },
        [onChange, actions.setValue]
    );

    /**
     * Resolves optional metadata for a completed temporary GUID and publishes only a current attempt.
     * Postconditions: The GUID is tracked before awaiting metadata. Metadata lookup failures are ignored;
     * stale allocations are removed when possible, with cleanup errors logged. Current completion updates
     * the parsed success value before clearing upload metadata; parsing/update failures propagate.
     */
    const completeUpload = useCallback(
        async (guid: string, attempt: number) => {
            // Track the allocation before awaiting metadata, so unmount can see it.
            temporaryFileIdsRef.current.add(guid);
            let temporaryFile: TemporaryFile | undefined;
            if (isCurrentAttempt(attempt)) {
                try {
                    const response = await client.get(new GetTemporaryFileInfoRequest({ guid }));
                    temporaryFile = response.temporaryFile;
                } catch {
                    // Metadata is optional; a current upload can still be saved by GUID.
                }
            }
            if (!isCurrentAttempt(attempt)) {
                try {
                    await client.post(new RemoveTemporaryFileRequest({ guid }));
                    temporaryFileIdsRef.current.delete(guid);
                } catch (error) {
                    console.error("Failed to remove superseded temporary file:", error);
                }
                return;
            }
            update(parseValue(createSuccessState(guid, temporaryFile)));
            setUploadState(undefined);
        },
        [client, isCurrentAttempt, update, parseValue, setUploadState]
    );

    /**
     * Parses an XHR response and delegates successful GUID completion.
     * Postconditions: Missing GUID, server error or caught parsing/completion failure marks only the
     * current attempt failed; stale failures do not replace current upload state.
     */
    const processResponse = useCallback(
        async (response: string, attempt: number) => {
            try {
                const result: UploadTemporaryFileResponse = JSON.parse(response);
                if (!result.responseStatus?.errorCode && result.guid) {
                    await completeUpload(result.guid, attempt);
                } else if (isCurrentAttempt(attempt)) {
                    setUploadState({ state: "failed" });
                }
            } catch {
                if (isCurrentAttempt(attempt)) {
                    setUploadState({ state: "failed" });
                }
            }
        },
        [completeUpload, isCurrentAttempt, setUploadState]
    );

    /**
     * Aborts the previous XHR and dispatches a multipart temporary-file request.
     * Postconditions: Returns after send, before transfer completion. Current computable progress updates
     * metadata; load checks HTTP status before response parsing, and network errors mark current failure.
     * Abort handling belongs to its caller. Synchronous setup/send failures propagate.
     */
    const xhrUpload = useCallback(
        (newFile: File, attempt: number) => {
            xhrRef.current?.abort();

            const xhr = new XMLHttpRequest();
            xhrRef.current = xhr;

            xhr.upload.addEventListener(
                "progress",
                (event: ProgressEvent) => {
                    if (isCurrentAttempt(attempt) && event.lengthComputable) {
                        const loadedBytes = event.loaded; // Current transferred bytes
                        const totalBytes = event.total; // Total bytes expected
                        const percent = Math.floor((loadedBytes / totalBytes) * 100);
                        setUploadState({ state: "uploading", progress: percent });
                    }
                }
            );

            // A completed HTTP response still needs status and payload checks.
            xhr.addEventListener(
                "load",
                async (_event: Event) => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        await processResponse(xhr.responseText, attempt);
                    } else if (isCurrentAttempt(attempt)) {
                        setUploadState({ state: "failed" });
                    }
                }
            );

            // Fires if the request fails at the network level
            xhr.addEventListener(
                "error",
                () => {
                    //console.error('Network error occurred.');
                    if (isCurrentAttempt(attempt)) {
                        setUploadState({ state: "failed" });
                    }
                }
            );

            // Fires if xhr.abort() is explicitly called
            xhr.addEventListener(
                "abort",
                () => {
                    // console.warn('Upload cancelled by the user.');
                    // This will be handled where abort is called (could be a cancel, could be an automated cancel of old request).
                }
            );

            xhr.open("post", "/api/v2/temporary-file/upload?format=json", true);
            var formData = new FormData();
            formData.append("file", newFile, newFile.name);
            formData.append("json", "true");
            xhr.send(formData);
        },
        [processResponse, setUploadState, isCurrentAttempt]
    );

    /**
     * Allocates and runs a chunked upload while mapping progress, cancellation and completion callbacks.
     * Postconditions: Cancellation advances the attempt token; later events cannot complete the old
     * attempt. A superseded allocation is cancelled without notification. Caught current failures publish
     * failed state; promise fulfillment alone does not establish successful file completion.
     */
    const chunkedUpload = useCallback(
        async (newFile: File, attempt: number) => {
            let uploadInfo: { uploadId: string | undefined } = {
                uploadId: undefined
            };
            let uploadProgress: number | undefined;
            let cancelledAttempt: number | undefined;

            try {
                uploadInfo.uploadId = await chunkedUploader.createFileUpload(
                    newFile,
                    async progress => {
                        if (!isCurrentAttempt(attempt)) {
                            return;
                        }
                        uploadProgress = progress;
                        setUploadState({
                            uploadId: uploadInfo.uploadId,
                            state: "uploading",
                            progress
                        });
                    },
                    async state => {
                        if (state === "cancelling" && isCurrentAttempt(attempt)) {
                            cancelledAttempt = ++attemptRef.current;
                        }
                        if (cancelledAttempt !== undefined) {
                            if (isCurrentAttempt(cancelledAttempt)) {
                                setUploadState(
                                    state === "cancelled"
                                        ? undefined
                                        : {
                                              uploadId: uploadInfo.uploadId,
                                              state,
                                              progress: uploadProgress
                                          }
                                );
                                if (state === "cancelled") {
                                    activeChunkRef.current = undefined;
                                }
                            }
                            return;
                        }
                        if (state === "completed" && uploadInfo.uploadId) {
                            await completeUpload(uploadInfo.uploadId, attempt);
                            if (activeChunkRef.current?.id === uploadInfo.uploadId) {
                                activeChunkRef.current = undefined;
                            }
                            return;
                        }
                        if (!isCurrentAttempt(attempt)) {
                            return;
                        }
                        setUploadState({
                            uploadId: uploadInfo.uploadId,
                            state,
                            progress: uploadProgress
                        });

                        if (state === "cancelled") {
                            attemptRef.current++;
                            activeChunkRef.current = undefined;
                            setUploadState(undefined);
                        }
                    }
                );

                if (!isCurrentAttempt(attempt)) {
                    if (uploadInfo.uploadId) {
                        await chunkedUploader.cancel(uploadInfo.uploadId, false);
                    }
                    return;
                }
                if (!uploadInfo.uploadId) {
                    setUploadState({ state: "failed" });
                    return;
                }
                activeChunkRef.current = { uploader: chunkedUploader, id: uploadInfo.uploadId };

                setUploadState({ uploadId: uploadInfo.uploadId, state: "idle" });

                await chunkedUploader.upload(newFile, uploadInfo.uploadId);
            } catch {
                if (!isCurrentAttempt(attempt)) {
                    return;
                }
                if (uploadInfo.uploadId) {
                    setUploadState({ uploadId: uploadInfo.uploadId, state: "failed" });
                } else {
                    setUploadState({ state: "failed" });
                }
            }
        },
        [chunkedUploader, completeUpload, isCurrentAttempt, setUploadState]
    );

    /**
     * Starts a replacement attempt or removes a current temporary upload when no single file is supplied.
     * Postconditions: Invalidates old callbacks, aborts XHR and cancels any previous chunk allocation first.
     * Cancellation/removal failures are logged and stop further work. Clearing removes the current GUID
     * before resetting the field; an array is treated as clearing. New files choose chunks only when
     * chunking is enabled and size exceeds chunkSize. XHR returns after dispatch; chunk mode awaits its
     * uploader path, which catches failures. Fulfillment does not guarantee upload or persistence.
     */
    const upload = useCallback(
        async (newFile: File | File[] | undefined) => {
            const attempt = ++attemptRef.current;
            xhrRef.current?.abort();
            xhrRef.current = undefined;

            const previousChunk = activeChunkRef.current;
            if (previousChunk) {
                try {
                    await previousChunk.uploader.cancel(previousChunk.id, false);
                    if (activeChunkRef.current === previousChunk) {
                        activeChunkRef.current = undefined;
                    }
                } catch (error) {
                    console.error("Failed to clean up the previous chunked upload:", error);
                    return;
                }
            }
            if (!isCurrentAttempt(attempt)) {
                return;
            }

            if (!newFile || newFile instanceof Array) {
                const temporaryFileId = convertValue(snapshot.value)?.guid;
                if (temporaryFileId) {
                    try {
                        await client.post(
                            new RemoveTemporaryFileRequest({
                                guid: temporaryFileId
                            })
                        );
                        temporaryFileIdsRef.current.delete(temporaryFileId);
                    } catch (error) {
                        console.error("Failed to remove the temporary file:", error);
                        return;
                    }
                }

                if (!isCurrentAttempt(attempt)) {
                    return;
                }

                setFile(undefined);
                setUploadState(undefined);

                update(parseValue(createEmptyState()));

                return;
            }

            setFile(newFile);
            setUploadState({ state: "uploading" });

            if (chunk && newFile.size > chunkSize) {
                await chunkedUpload(newFile, attempt);
            } else {
                xhrUpload(newFile, attempt);
            }
        },
        [
            xhrRef,
            isCurrentAttempt,
            convertValue,
            snapshot.value,
            client,
            update,
            parseValue,
            setFile,
            setUploadState,
            chunkedUpload,
            xhrUpload
        ]
    );

    if (!view) {
        return null;
    }

    const View = view;

    return (
        <View
            {...viewProps}
            fieldInput={fieldInput}
            chunk={chunk}
            chunkSize={chunkSize}
            chunkConcurrencyLimit={chunkConcurrencyLimit}
            chunkRetryLimit={chunkRetryLimit}
            uploadState={uploadState}
            chunkedUploader={chunkedUploader}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            existingFile={existingFile}
            file={file}
            setFile={setFile}
            upload={upload}
            disabled={disabled}
        />
    );
};
